import { useState, useEffect, useMemo } from "react";
import { useConfirm } from "@/contexts/confirm-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";
import { EditCustomerDialog } from "@/components/edit-customer-dialog";
import { CustomerProfileDialog } from "@/components/customer-info-dialog";

import { Trash2, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Pencil, ShieldBan, ShieldCheck, User, ChevronDown, ChevronUp, Phone, Filter } from "lucide-react";

interface CustomerTableProps {
  customers: Customer[];
  products: Product[];
  jobs: string[];
  onDelete: (id: string) => void;
  onUpdateCustomer: (customer: Customer) => Promise<void> | void;
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => Promise<void> | void;
  onUpdateProduct: (product: Product) => Promise<void> | void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
  onViewProfile?: (customer: Customer) => void;

  mode?: "customers" | "payments"; // customers = بێ کلیک و بە دەستکاری، payments = بە کلیک و بێ دەستکاری
}

export function CustomerTable({ customers, products, jobs, onDelete, onUpdateCustomer, onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate, onViewProfile, mode = "payments" }: CustomerTableProps) {
  const { confirm } = useConfirm();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState<Customer | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  
  // خوێندنەوەی ڕیزکردن لە localStorage
  const [sortOption, setSortOption] = useState<string>(() => {
    return localStorage.getItem('customer-sort-option') || 'none';
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // تۆمارکردنی ڕیزکردن لە localStorage کاتێک دەگۆڕێت
  useEffect(() => {
    localStorage.setItem('customer-sort-option', sortOption);
  }, [sortOption]);

  const sortOptions = [
    { value: 'none', label: 'بێ ڕیزکردن', icon: Filter },
    { value: 'code-asc', label: 'کۆد: لە بچووک بۆ گەورە', icon: ArrowUp },
    { value: 'code-desc', label: 'کۆد: لە گەورە بۆ بچووک', icon: ArrowDown },
    { value: 'name-asc', label: 'ناو: ئەلف - یا', icon: ArrowUp },
    { value: 'name-desc', label: 'ناو: یا - ئەلف', icon: ArrowDown },
  ];

  const toggleExpanded = (customerId: string) => {
    setExpandedCustomers(prev => {
      const newSet = new Set<string>();
      if (!prev.has(customerId)) {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    if (onViewProfile) {
      onViewProfile(customer);
    } else {
      setSelectedCustomer(customer);
      setProfileOpen(true);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleToggleBlock = (customer: Customer) => {
    onUpdateCustomer({
      ...customer,
      isBlocked: !customer.isBlocked
    });
  };

  const handleShowProfile = (customer: Customer) => {
    if (onViewProfile) {
      onViewProfile(customer);
    } else {
      setProfileCustomer(customer);
      setProfileDialogOpen(true);
    }
  };

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      switch (sortOption) {
        case 'code-asc':
          return Number(a.customerCode) - Number(b.customerCode);
        case 'code-desc':
          return Number(b.customerCode) - Number(a.customerCode);
        case 'name-asc':
          return a.fullName.localeCompare(b.fullName, 'ar');
        case 'name-desc':
          return b.fullName.localeCompare(a.fullName, 'ar');
        default:
          return 0; // بێ ڕیزکردن
      }
    });
  }, [customers, sortOption]);

  // Pagination
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (customers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ کڕیارێک نییە
      </div>
    );
  }

  return (
    <>
      {/* Sort Filter Button */}
      <div className="mb-4 flex items-center gap-2 px-2 md:px-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">
                {sortOptions.find(opt => opt.value === sortOption)?.label || 'ڕیزکردن'}
              </span>
              <span className="sm:hidden">ڕیزکردن</span>
              <ChevronDown className="h-3 w-3 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {sortOptions.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortOption(option.value)}
                  className={sortOption === option.value ? 'bg-primary/10 text-primary' : ''}
                >
                  <Icon className="h-4 w-4 ml-2" />
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        
        {sortOption !== 'none' && (
          <Badge variant="secondary" className="text-xs hidden md:inline-flex">
            {sortOptions.find(opt => opt.value === sortOption)?.label}
          </Badge>
        )}
      </div>

      {/* Desktop Table */}
      <div className="rounded-md border hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">کۆد</TableHead>
              <TableHead className="text-right">ناو</TableHead>
              <TableHead className="text-right">مۆبایل</TableHead>
              <TableHead className="text-right">کەفیل</TableHead>
              <TableHead className="text-right">ژمارەی کەفیل</TableHead>
              <TableHead className="text-right">پیشە</TableHead>
              {mode === "customers" && <TableHead className="text-right">دۆخ</TableHead>}
              {mode === "customers" && <TableHead className="text-right w-fit"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCustomers.map((customer, index) => (
              <TableRow
                key={customer.id}
                className={`${mode === "payments" ? "cursor-pointer" : ""} ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
                onClick={mode === "payments" ? () => handleViewCustomer(customer) : undefined}
              >
                <TableCell>
                  <Badge variant="outline">{customer.customerCode}</Badge>
                </TableCell>
                <TableCell className="font-medium">{customer.fullName}</TableCell>
                <TableCell dir="ltr" className="text-right">{customer.phone1}</TableCell>
                <TableCell>{customer.guarantorName || "-"}</TableCell>
                <TableCell dir="ltr" className="text-right">{customer.guarantorPhone || "-"}</TableCell>
                <TableCell>
                  {customer.job ? <Badge variant="secondary">{customer.job}</Badge> : "-"}
                </TableCell>
                {mode === "customers" && (
                  <TableCell>
                    {customer.isBlocked ? (
                      <Badge variant="destructive" className="text-xs">بلۆک کراو</Badge>
                    ) : (
                      <Badge variant="default" className="text-xs bg-green-600">چالاک</Badge>
                    )}
                  </TableCell>
                )}
                {mode === "customers" && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                        <MoreHorizontal className="h-4 w-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="text-right w-48">
                        <DropdownMenuItem onSelect={() => handleShowProfile(customer)}>
                          <User className="h-4 w-4 ml-2" />
                          پرۆفایلی کڕیار
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleEditCustomer(customer)}>
                          <Pencil className="h-4 w-4 ml-2" />
                          دەستکاریکردن
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => handleToggleBlock(customer)}>
                          {customer.isBlocked ? (
                            <>
                              <ShieldCheck className="h-4 w-4 ml-2 text-green-600" />
                              <span className="text-green-600">لابردنی بلۆک</span>
                            </>
                          ) : (
                            <>
                              <ShieldBan className="h-4 w-4 ml-2 text-amber-600" />
                              <span className="text-amber-600">بلۆککردن</span>
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {(() => {
                          const customerProducts = products.filter(p => p.customerId === customer.id);
                          const hasProducts = customerProducts.length > 0;
                          return (
                            <DropdownMenuItem
                              disabled={hasProducts}
                              onSelect={async () => {
                                await confirm({
                                  title: "سڕینەوەی کڕیار",
                                  description: "دڵنیایت لە سڕینەوەی ئەم کڕیارە؟",
                                  variant: "destructive",
                                  confirmText: "بەڵێ، بیسڕەوە",
                                  cancelText: "نەخێر",
                                  onConfirm: async () => {
                                    await onDelete(customer.id);
                                  }
                                });
                              }}
                              className={hasProducts ? "text-muted-foreground cursor-not-allowed" : "text-destructive focus:text-destructive"}
                            >
                              <Trash2 className="h-4 w-4 ml-2" />
                              {hasProducts ? "کاڵای لەسەر هەیە" : "سڕینەوە"}
                            </DropdownMenuItem>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={sortedCustomers.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {paginatedCustomers.map((customer, index) => {
          const isExpanded = expandedCustomers.has(customer.id);
          return (
            <div
              key={customer.id}
              className={`rounded-xl border bg-card overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${index % 2 === 1 ? 'bg-muted/60' : ''}`}
            >
              {/* Main Row - Always Visible */}
              <div
                className="p-3 flex items-center gap-3 cursor-pointer"
                onClick={() => mode === "payments" ? handleViewCustomer(customer) : toggleExpanded(customer.id)}
              >
                {/* Avatar with Code */}
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">{customer.customerCode}</span>
                </div>

                {/* Name and Phone */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{customer.fullName}</span>
                    {customer.isBlocked ? (
                      <Badge variant="destructive" className="text-[10px] px-1.5 h-4">بلۆک</Badge>
                    ) : (
                      <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">چالاک</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Phone className="h-3 w-3" />
                    <span dir="ltr">{customer.phone1}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {mode === "customers" && (
                    <>
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors cursor-pointer">
                            <Phone className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-right w-56">
                            <div dir="rtl">
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                ژمارەکانی پەیوەندی
                              </div>
                              <DropdownMenuSeparator />
                              <div className="bg-muted/40 p-1 rounded-md space-y-1 mx-1 mb-1">
                                <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                  <a
                                    href={`tel:${customer.phone1}`}
                                    className="flex items-center gap-2 cursor-pointer w-full"
                                  >
                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                      <Phone className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-[10px] text-muted-foreground">مۆبایل ١</div>
                                      <div className="font-medium text-sm" dir="ltr">{customer.phone1}</div>
                                    </div>
                                  </a>
                                </DropdownMenuItem>
                                {customer.phone2 && (
                                  <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                    <a
                                      href={`tel:${customer.phone2}`}
                                      className="flex items-center gap-2 cursor-pointer w-full"
                                    >
                                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                        <Phone className="h-4 w-4 text-blue-600" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="text-[10px] text-muted-foreground">مۆبایل ٢</div>
                                        <div className="font-medium text-sm" dir="ltr">{customer.phone2}</div>
                                      </div>
                                    </a>
                                  </DropdownMenuItem>
                                )}
                                {customer.guarantorPhone && (
                                  <>
                                    <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                      <a
                                        href={`tel:${customer.guarantorPhone}`}
                                        className="flex items-center gap-2 cursor-pointer w-full"
                                      >
                                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                                          <Phone className="h-4 w-4 text-amber-600" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-[10px] text-muted-foreground">کەفیل</div>
                                          <div className="font-medium text-sm" dir="ltr">{customer.guarantorPhone}</div>
                                        </div>
                                      </a>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </div>
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                            <MoreHorizontal className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent alignOffset={-50} className="text-right w-48">
                            <DropdownMenuItem onSelect={() => handleShowProfile(customer)}>
                              <User className="h-4 w-4 ml-2" />
                              پرۆفایلی کڕیار
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleEditCustomer(customer)}>
                              <Pencil className="h-4 w-4 ml-2" />
                              دەستکاریکردن
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => handleToggleBlock(customer)}>
                              {customer.isBlocked ? (
                                <>
                                  <ShieldCheck className="h-4 w-4 ml-2 text-green-600" />
                                  <span className="text-green-600">لابردنی بلۆک</span>
                                </>
                              ) : (
                                <>
                                  <ShieldBan className="h-4 w-4 ml-2 text-amber-600" />
                                  <span className="text-amber-600">بلۆککردن</span>
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {(() => {
                              const customerProducts = products.filter(p => p.customerId === customer.id);
                              const hasProducts = customerProducts.length > 0;
                              return (
                                <DropdownMenuItem
                                  disabled={hasProducts}
                                  onSelect={async () => {
                                    await confirm({
                                      title: "سڕینەوەی کڕیار",
                                      description: "دڵنیایت لە سڕینەوەی ئەم کڕیارە؟",
                                      variant: "destructive",
                                      confirmText: "بەڵێ، بیسڕەوە",
                                      cancelText: "نەخێر",
                                      onConfirm: async () => {
                                        await onDelete(customer.id);
                                      }
                                    });
                                  }}
                                  className={hasProducts ? "text-muted-foreground cursor-not-allowed" : "text-destructive focus:text-destructive"}
                                >
                                  <Trash2 className="h-4 w-4 ml-2" />
                                  {hasProducts ? "کاڵای لەسەر هەیە" : "سڕینەوە"}
                                </DropdownMenuItem>
                              );
                            })()}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {mode === "customers" && isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                  <div className="space-y-2 pt-3">
                    {customer.phone2 && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">مۆبایل ٢</span>
                        <span className="font-medium text-sm" dir="ltr">{customer.phone2}</span>
                      </div>
                    )}
                    {customer.job && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">پیشە</span>
                        <span className="font-medium text-sm">{customer.job}</span>
                      </div>
                    )}
                    {customer.guarantorName && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">کەفیل</span>
                        <span className="font-medium text-sm">{customer.guarantorName}</span>
                      </div>
                    )}
                    {customer.guarantorPhone && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ژمارەی کەفیل</span>
                        <span className="font-medium text-sm" dir="ltr">{customer.guarantorPhone}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ناونیشان</span>
                        <span className="font-medium text-sm">{customer.address}</span>
                      </div>
                    )}
                    {customer.notes && (
                      <div className="pt-2 mt-1 border-t">
                        <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                        <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={sortedCustomers.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      <CustomerPaymentSheet
        customer={selectedCustomer}
        products={products}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onAddProduct={onAddProduct}
        onDeleteProduct={onDeleteProduct}
        onUpdateProduct={onUpdateProduct}
        onNavigate={onNavigate}
      />

      {/* Edit Customer Dialog - triggered from dropdown */}
      {editCustomer && (
        <EditCustomerDialog
          customer={editCustomer}
          onUpdate={async (updatedCustomer) => {
            await onUpdateCustomer(updatedCustomer);
            setEditDialogOpen(false);
            setEditCustomer(null);
          }}
          jobs={jobs}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditCustomer(null);
          }}
        />
      )}


      {/* Customer Profile Dialog */}
      <CustomerProfileDialog
        customer={profileCustomer}
        products={products}
        open={profileDialogOpen}
        onOpenChange={(open) => {
          setProfileDialogOpen(open);
          if (!open) setProfileCustomer(null);
        }}
        onUpdateProduct={onUpdateProduct}
      />
    </>
  );
}
